import { bind } from '@react-rxjs/core';
import {
  combineKeys,
  createKeyedSignal,
  createSignal,
  mergeWithKey,
  partitionByKey,
  toKeySet,
} from '@react-rxjs/utils';
import { matchPath } from 'react-router-dom';
import {
  OperatorFunction,
  catchError,
  combineLatest,
  concatMap,
  filter,
  from,
  iif,
  map,
  merge,
  mergeMap,
  of,
  pipe,
  pluck,
  scan,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import {
  AnswerFile,
  AnswerState,
  Choice,
  ClosedAnswerState,
  ClosedQuestion,
  MalfunctionPointer,
  OpenAnswerState,
  Question,
  QuestionType,
  QuestionnaireItem,
  QuestionnairePayload,
  SelectedImage,
  ServerResponse,
  SignatureAnswerState,
} from '../pages/Inspection/types';
import { Progress, UUID } from '../types';
import { resizeFile } from '../utils/resizeFile';
import { explodeProp, truthyObs } from '../utils/rxjs/misc';
import { pathname$ } from './browserHistory';
import { request } from './request';
import { assignVehicle$, vehicle$ } from './vehicle';

export const MAX_FILES_PER_QUESTION = 10;
export const MAX_IMAGE_SIZE = 2000;

export const [questionText, updateQuestionText] = createKeyedSignal<
  UUID,
  string
>();
export const [addFiles, addQuestionFiles] = createKeyedSignal<
  UUID,
  FileList | File[] | null
>();
export const [removeFile, removeQuestionFiles] = createKeyedSignal<
  UUID,
  number
>();
export const [questionChoice, updateQuestionChoice] = createKeyedSignal<
  UUID,
  UUID
>();
export const [addMalfunction, addQuestionMalfunction] = createKeyedSignal<
  UUID,
  MalfunctionPointer
>();

const mapImages = (
  questionId: UUID
): OperatorFunction<FileList | File[] | null, SelectedImage[]> =>
  pipe(
    filter((files): files is FileList | File[] => !!files),
    map((files) => Array.from(files)),
    switchMap((files) => {
      const images = files.map(
        (file) =>
          new Promise<SelectedImage>((resolve) => {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
              resolve({ file, src: reader.result as string, questionId });
            });
            reader.readAsDataURL(file);
          })
      );
      return from(Promise.all(images));
    })
  );

export const [refetchQuestionnaires$, refetchQuestionnaires] = createSignal();

export const [useQuestionnaires, questionnaires$] = bind(
  refetchQuestionnaires$.pipe(
    startWith(null),
    switchMap(() =>
      vehicle$.pipe(
        switchMap((assignation) =>
          assignation
            ? request<ServerResponse>(
                `/gateway/vehicle-inspection-service/v20220701/assigned-questionnaires/${assignation.id}`
              ).pipe(catchError(() => of([])))
            : of([])
        ),
        shareReplay(1)
      )
    )
  )
);

export const pickedQuestionnaire$ = pathname$.pipe(
  map((path) => matchPath({ path: '/inspection/:id' }, path)),
  map((match) => match?.params.id)
);

export const questionnaire$ = questionnaires$.pipe(
  switchMap((items) =>
    pickedQuestionnaire$.pipe(
      map((id) => (id ? items.find((item) => item.id === id) ?? null : null))
    )
  )
);

export const [useQuestionnaire] = bind<QuestionnaireItem | 'loading' | null>(
  questionnaire$,
  'loading'
);

export const [quesitonById, questions$] = partitionByKey<Question, UUID>(
  truthyObs(questionnaire$).pipe(explodeProp('payload', 'questions')),
  (question) => question.id
);

export const [useQuestionById, question$] = bind(quesitonById);

export const [choicesById, choices$] = partitionByKey<Choice, UUID>(
  truthyObs(questionnaire$).pipe(explodeProp('payload', 'choices')),
  (choice) => choice.id
);

export const imagesByQId = (id: UUID) =>
  mergeWithKey({
    add: addFiles(id).pipe(mapImages(id)),
    remove: removeFile(id),
  }).pipe(
    scan((acc, action) => {
      if (action.type === 'remove') {
        acc.splice(action.payload, 1);
        return acc;
      }
      return acc.concat(action.payload).slice(0, MAX_FILES_PER_QUESTION);
    }, [] as SelectedImage[]),
    startWith([])
  );

export const malfunctionsByQId = (id: UUID) =>
  addMalfunction(id).pipe(
    scan((acc, malfunction) => {
      return acc.concat([malfunction]);
    }, [] as MalfunctionPointer[]),
    startWith([])
  );

export const [useAnswers, answers$] = bind<AnswerState>(
  questions$.pipe(
    toKeySet(),
    switchMap((questions) =>
      merge(
        ...Array.from(questions).map((id) =>
          combineLatest([
            questionText(id).pipe(startWith('')),
            imagesByQId(id),
            questionChoice(id).pipe(startWith(undefined)),
            quesitonById(id),
            malfunctionsByQId(id),
          ]).pipe(
            map(([answer, files, choice, question, malfunctions]) => ({
              answer,
              files,
              question: id,
              type: question.type,
              choice: choice!,
              malfunctions,
            }))
          )
        )
      )
    )
  )
);

export const [answerById, answerKeys$] = partitionByKey<AnswerState, UUID>(
  answers$,
  (answer) => answer.question
);

export const [nextPage$, nextPage] = createSignal();
export const [previousPage$, previousPage] = createSignal();

export const [useInspectionPages, inspectionPages$] = bind(
  mergeWithKey({
    total: truthyObs(questions$).pipe(
      toKeySet(),
      map((questions) => questions.size)
    ),
    nextPage: nextPage$,
    previousPage: previousPage$,
  }).pipe(
    scan(
      (acc, action) => {
        switch (action.type) {
          case 'total':
            return {
              total: action.payload,
              current:
                acc.current > action.payload - 1 ? action.payload : acc.current,
            };
          case 'nextPage':
            return {
              ...acc,
              current:
                acc.current < acc.total - 1 ? acc.current + 1 : acc.current,
            };
          case 'previousPage':
            return {
              ...acc,
              current: acc.current > 0 ? acc.current - 1 : acc.current,
            };
          default:
            return acc;
        }
      },
      { total: 0, current: 0 }
    )
  ),
  { total: 0, current: 0 }
);

export const [useCurrentQuestion, currentQuestion$] = bind(
  truthyObs(questionnaire$).pipe(
    switchMap((questionnaire) =>
      inspectionPages$.pipe(
        map(
          ({ current }) =>
            questionnaire.payload.questionnaire.questions[current]
        )
      )
    ),
    switchMap(quesitonById)
  )
);

export const [useCurrentAnswer, currentAnswer$] = bind<AnswerState>(
  truthyObs(currentQuestion$).pipe(pluck('id'), switchMap(answerById))
);

export const [useCurrentChoices, currentChoices$] = bind<Choice[]>(
  truthyObs(currentQuestion$).pipe(
    filter(
      (question): question is ClosedQuestion =>
        question.type === QuestionType.CLOSED
    ),
    pluck('choices'),
    switchMap((choices) => combineLatest(choices.map(choicesById)))
  ),
  []
);

export const [useIsCurrentAnswerValid, currentAnswerValid$] = bind<boolean>(
  combineLatest([truthyObs(currentQuestion$), truthyObs(currentAnswer$)]).pipe(
    map(([question, answer]) => {
      if (
        question.type === QuestionType.CLOSED &&
        (answer as ClosedAnswerState).choice != null
      ) {
        return true;
      }

      if (
        question.type === QuestionType.OPEN &&
        (answer as OpenAnswerState).answer !== ''
      ) {
        return true;
      }

      if (
        question.type === QuestionType.SIGNATURE &&
        (answer as SignatureAnswerState).files.length > 0
      ) {
        return true;
      }

      if (question.type === QuestionType.MALFUNCTION) {
        return true;
      }

      return false;
    })
  ),
  false
);

export const [submitSignal$, submitQuestionnaire] = createSignal();

type QuestionnaireUploadFilesState = [SelectedImage, AnswerFile];

interface QuestionnaireUploadState extends Progress {
  files: QuestionnaireUploadFilesState[];
  payload?: QuestionnairePayload;
  error?: string;
  running?: boolean;
}

const uploadFile = (file: File, objectId: number) => {
  const formData = new FormData();
  formData.append('file', file);

  return request<AnswerFile>(
    `/gateway/vehicle-inspection-service/v20220701/filled-for-object/${objectId}/questionnaire-photo`,
    {
      method: 'POST',
      body: formData,
    }
  );
};

export const [addMalfunctionToCurrentQ$, addMalfunctionToCurrentQIfExists] =
  createSignal<MalfunctionPointer>();
export const [useConnectMalfunctions] = bind(
  currentQuestion$.pipe(
    switchMap((question) =>
      addMalfunctionToCurrentQ$.pipe(
        tap((malfunction) => {
          addQuestionMalfunction(question.id, malfunction);
        }),
        map(() => null)
      )
    )
  ),
  null
);

/**
 * handles questionnaire upload while indicating file upload progress
 * first uploads images,
 * then constructs questionnaire upload payload using ids from uploaded images
 * then saves questionnaire
 */
export const [useSubmitState, submit$] = bind<
  [],
  QuestionnaireUploadState | null
>(
  () =>
    combineLatest([
      questionnaire$,
      combineKeys(answerKeys$, answerById).pipe(
        map((answerMap) => Array.from(answerMap, ([, value]) => value))
      ),
      vehicle$,
    ]).pipe(
      switchMap(([questionnaire, answers, assignation]) =>
        submitSignal$.pipe(
          filter(() => !!questionnaire && !!assignation),
          /** collect all files */
          map(() =>
            answers.reduce<SelectedImage[]>(
              (acc, value) => acc.concat(value.files),
              []
            )
          ),
          switchMap((files) =>
            iif(
              () => files.length < 1,
              of<QuestionnaireUploadState>({
                total: 0,
                progress: 0,
                files: [],
                running: true,
              }),
              from(files).pipe(
                concatMap((file) =>
                  from(resizeFile(file.file, MAX_IMAGE_SIZE)).pipe(
                    map((resized) => ({ ...file, file: resized }))
                  )
                ),
                mergeMap((file) =>
                  uploadFile(file.file, assignation!.id).pipe(
                    map<AnswerFile, QuestionnaireUploadFilesState>(
                      (answerFile) => [file, answerFile]
                    )
                  )
                ),
                /** update file upload progress */
                scan<QuestionnaireUploadFilesState, QuestionnaireUploadState>(
                  (acc, value) => {
                    const newAcc = { ...acc };
                    newAcc.progress = newAcc.progress! + 1;
                    newAcc.files!.push(value);
                    return newAcc;
                  },
                  { total: files.length, progress: 0, files: [], running: true }
                ),
                startWith<QuestionnaireUploadState>({
                  total: files.length,
                  progress: 0,
                  files: [],
                  running: true,
                }),
                catchError(() => {
                  return of<QuestionnaireUploadState>({
                    error: 'UPLOAD_ERROR',
                    running: false,
                    files: [],
                    total: 0,
                  });
                })
              )
            )
          ),
          switchMap((state) =>
            iif(
              () => !!state.error || (state.progress ?? 0) < state.total,
              of(state),
              of(state).pipe(
                /** create questionnaire payload while mapping uploaded images to answers */
                map<QuestionnaireUploadState, QuestionnaireUploadState>(
                  (state) => {
                    return {
                      ...state,
                      payload: {
                        ...questionnaire!.payload,
                        files: state.files.map(([, file]) => file),
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        answers: answers.map(({ files, ...answer }) => ({
                          ...answer,
                          files: state.files
                            .filter(
                              ([selectedFile]) =>
                                selectedFile.questionId === answer.question
                            )
                            .map(([, uploadedFile]) => uploadedFile.id),
                        })),
                      },
                    };
                  }
                ),
                switchMap((state) =>
                  request<boolean>(
                    `/gateway/vehicle-inspection-service/v20220701/filled-for-object/${
                      assignation!.id
                    }/questionnaire/${questionnaire!.id}`,
                    {
                      method: 'POST',
                      body: JSON.stringify(state.payload),
                      headers: { 'Content-Type': 'application/json' },
                    }
                  ).pipe(
                    map(() => ({ ...state, running: false, error: undefined })),
                    catchError(() => {
                      return of({
                        ...state,
                        error: 'SAVE_ERROR',
                        running: false,
                      });
                    })
                  )
                ),
                startWith(state)
              )
            )
          )
        )
      ),
      tap((state) => {
        if (!state.error && !state.running) {
          refetchQuestionnaires();
        }
      })
    ),
  null
);

export const [questionnaireAlertSeen, markQuestionnaireAlertSeen] =
  createSignal();

/** gets first questionnaire only after user assigns vehicle */
export const [useQuestionnaireAlert] = bind(
  merge(
    assignVehicle$.pipe(
      switchMap(() => questionnaires$),
      filter((items) => items.length > 0),
      map((items) => items[0])
    ),
    questionnaireAlertSeen.pipe(map(() => null))
  ),
  null
);
